import { Box, IconButton, styled, TableCell, Tooltip, tooltipClasses, TooltipProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { memo } from 'react';
import { cast } from '~/utils/helper';
import { EditLineIcon, DeleteBin6LineIcon, Information4Icon } from '~/assets/icons';
import dayjs from 'dayjs';
import BaseTable from '~/components/common/base-table/BaseTable';
import { HeadCelType, SortDirection } from '~/utils/types/common';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { PAGE_INDEX, PAGE_SIZE } from '~/utils/constants/common';
import { ImeiEnum } from '~/utils/enum';
import { ILocation } from '~/utils/interface/location';
import { locationActions } from '~/thunks/location/locationSlice';

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: '#404854',
    boxShadow: '0px 4px 12px #509EE833',
    border: 1,
    fontSize: theme.typography.pxToRem(15),
  },
}));

type LocationListProps = {
  onDetail?: (row: ILocation) => void;
  onOpenEdit?: (row: ILocation) => void;
  onOpenDelete?: (row: ILocation) => void;
};

const LocationList = (props: LocationListProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('admin/location');

  const { locations, totalItems, filter } = useAppSelector((state) => state.location);

  const headCells: Array<HeadCelType> = [
    {
      id: 'id',
      label: t('stt'),
      sortable: false,
      searchable: false,
      className: 'w-16 border-l border-[#EBF1F8] border-b-0 sticky left-0 z-10',
    },
    {
      id: 'action',
      label: t('action', { ns: 'admin/common' }),
      sortable: false,
      searchable: false,
      className: 'border-l border-white w-24 border-b-0 sticky left-12 z-10',
    },
    {
      id: 'name',
      label: t('name'),
      sortable: true,
      searchable: true,
      className: 'border-l border-white w-40 border-b-0',
    },
    {
      id: 'location_type',
      label: t('type_location'),
      sortable: true,
      searchable: false,
      className: 'border-l border-white w-40 border-b-0',
    },
    {
      id: 'ward_name',
      label: t('province_ward_id'),
      sortable: false,
      searchable: false,
      className: 'border-l border-[#EBF1F8] w-56 border-b-0',
    },
    {
      id: 'created_by',
      label: t('created_by'),
      sortable: true,
      searchable: true,
      className: 'border-l border-white w-56 border-b-0',
    },
    {
      id: 'is_assigned',
      label: t('is_assigned.title'),
      sortable: true,
      searchable: false,
      className: 'border-l border-white w-36 border-b-0',
    },
    {
      id: 'created_at',
      label: t('created_at'),
      sortable: true,
      searchable: false,
      className: 'border-l border-white w-36 border-b-0',
    },
  ];

  const getAssigned = (value: number) => {
    if (value === ImeiEnum.UNASSIGNED) {
      return (
        <Box className='text-[#7B8794] bg-[#7B87941A] text-base w-28 text-center font-normal rounded-full py-2'>
          {t('is_assigned.' + ImeiEnum.UNASSIGNED)}
        </Box>
      );
    } else {
      return (
        <Box className='text-[#17A62B] bg-[#00B1351A] text-base w-24 text-center font-normal rounded-full py-2'>
          {t('is_assigned.' + ImeiEnum.ASSIGNED)}
        </Box>
      );
    }
  };

  const handleChangePage = (newPage: number) => {
    dispatch(
      locationActions.setFilter({
        pageIndex: newPage,
      })
    );
  };

  const handleChangePageSize = (pageSize: number) => {
    dispatch(
      locationActions.setFilter({
        pageSize: pageSize,
        pageIndex: PAGE_INDEX,
      })
    );
  };

  const handleRequestSort = (sortBy: string, sortOrder: SortDirection) => {
    dispatch(
      locationActions.setFilter({
        sortBy: sortBy,
        sortOrder: sortOrder,
        pageIndex: PAGE_INDEX,
      })
    );
  };

  const handleRequestSearch = (searchBy: string, value: string) => {
    dispatch(
      locationActions.setFilter({
        search: value,
        searchBy: searchBy,
        pageIndex: PAGE_INDEX,
      })
    );
  };

  return (
    <Box className='w-full'>
      <BaseTable<any>
        label={t('location')}
        data={locations}
        pageIndex={filter.pageIndex || PAGE_INDEX}
        pageSize={filter.pageSize || PAGE_SIZE}
        totalItems={totalItems}
        headCells={headCells}
        sortBy={filter.sortBy}
        sortOrder={filter.sortOrder}
        searchInput={filter.search}
        onChangePage={handleChangePage}
        onChangePageSize={handleChangePageSize}
        onRequestSort={handleRequestSort}
        onRequestSearch={handleRequestSearch}
        renderCell={(row: ILocation, index: number) => {
          return (
            <>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                align='center'
                sx={{ position: 'sticky', left: 0, zIndex: 1, backgroundColor: 'white' }}
              >
                {index + PAGE_INDEX + (Number(filter.pageIndex || 0) - PAGE_INDEX) * PAGE_SIZE}
              </TableCell>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                sx={{ position: 'sticky', left: 48, zIndex: 1, backgroundColor: 'white' }}
              >
                <Box className='flex justify-center'>
                  <LightTooltip title={t('tooltip.detail', { ns: 'admin/common' })}>
                    <IconButton onClick={() => props.onDetail?.(row)}>
                      <Information4Icon />
                    </IconButton>
                  </LightTooltip>
                  <LightTooltip title={t('tooltip.edit', { ns: 'admin/common' })}>
                    <IconButton onClick={() => props?.onOpenEdit?.(row)}>
                      <EditLineIcon />
                    </IconButton>
                  </LightTooltip>
                  <LightTooltip title={t('tooltip.delete', { ns: 'admin/common' })}>
                    <IconButton onClick={() => props.onOpenDelete?.(row)}>
                      <DeleteBin6LineIcon />
                    </IconButton>
                  </LightTooltip>
                </Box>
              </TableCell>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                align='center'
                sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
              >
                {cast(row.name)}
              </TableCell>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                align='center'
                sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
              >
                {t('location_type.' + row.location_type)}
              </TableCell>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                align='center'
              >{`${cast(row.ward_name ? row.ward_name + ' - ' : '')} ${cast(row.province_name)}`}</TableCell>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                align='center'
                sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
              >
                {cast(row.created_by)}
              </TableCell>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                align='center'
                sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
              >
                <Box className='flex justify-center'>{getAssigned(Number(row.is_assigned))}</Box>
              </TableCell>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                align='center'
                sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
              >
                {row.created_at ? dayjs(row.created_at).format('DD/MM/YYYY') : ''}
              </TableCell>
            </>
          );
        }}
      />
    </Box>
  );
};

export default memo(LocationList);
